// Admin › Returns: the BargainFox returns portal's requests, for the staff
// who handle refunds (admin.returns; Supervisors read-only by default).
//
// Tabs follow the work: Review (a person decides), Label to book, Awaiting
// return, In transit, To check (received at the warehouse), Done, All. Open
// tabs list the oldest first. The list is paginated on the server (never the
// whole table: an unbounded Orders list once OOM-killed Render). A request
// opens in a side panel; its reference rides the URL (?ref=BFR-…) so the
// Orders page can link straight to it.
//
// Errors (?tab=errors, set apart after the request tabs) lists what failed in
// the returns portal instead of requests (ReturnErrors.tsx); its badge, the
// errors nobody has marked seen yet (last 7 days), is read on every tab. The
// alert email links here.
//
// Returned to sender (2026-11-02): a search that is a dispatched parcel's
// label (a scan ends with Enter) also shows the "Returned parcel" panel above
// the list (ReturnedParcelPanel.tsx), to put its items on Pending Refund. The
// search box keeps the cursor with its text selected, so the next scan
// replaces it.
//
// New return (2026-11-04): staff who may write make a return for a customer
// (NewReturnDialog.tsx); once made it opens in the side panel. Such requests
// carry a "Manual" tag in the list.
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, RefreshCw, ChevronLeft, ChevronRight, Camera, Plus } from "lucide-react";
import AdminPageHeader from "../components/admin-page-header";
import { useTranslation } from "../i18n";
import { useAccess } from "../access/AccessContext";
import { useReadOnly } from "../access/ReadOnly";
import { isManagerPlus, readUserPosition } from "../access/positions";
import { RETURN_TABS, ReturnListPage, ReturnTab, listReturns, gbp, LabelPoolSummary, getLabelPoolSummary, getReturnErrors, ReturnErrorsSeen } from "../api/returns";
import { LowStockWarning } from "../components/returns/RoyalMailLabelPool";
import ReturnDetailPanel from "../components/returns/ReturnDetailPanel";
import ReturnedParcelPanel from "../components/returns/ReturnedParcelPanel";
import NewReturnDialog from "../components/returns/NewReturnDialog";
import ReturnErrors, { DEFAULT_ERROR_DAYS, ERROR_PERIODS, unseenErrorCount } from "../components/returns/ReturnErrors";
import { StatusPill, problemLabel, resolutionLabel } from "../components/returns/returnLabels";
import "../styles/admin-returns.css";

const PAGE_SIZE = 25;
const isTab = (v: string | null): v is ReturnTab => !!v && (RETURN_TABS as string[]).includes(v);

const AdminReturns = () => {
    const { t, language } = useTranslation();
    const [params, setParams] = useSearchParams();
    const onErrors = params.get("tab") === "errors";
    const tab: ReturnTab = isTab(params.get("tab")) ? (params.get("tab") as ReturnTab) : "review";
    const reference = params.get("ref");
    const applied = { q: params.get("q") ?? "", from: params.get("from") ?? "", to: params.get("to") ?? "" };
    const page = Math.max(1, Number(params.get("page")) || 1);
    // The Errors tab's filters (a period it doesn't offer falls back to 7 days; only new errors unless seen=all).
    const errArea = params.get("area") ?? "";
    const errDays = (ERROR_PERIODS as readonly number[]).includes(Number(params.get("days"))) ? Number(params.get("days")) : DEFAULT_ERROR_DAYS;
    const errSeen: ReturnErrorsSeen = params.get("seen") === "all" ? "all" : "new";

    const [search, setSearch] = useState(applied.q);
    const [from, setFrom] = useState(applied.from);
    const [to, setTo] = useState(applied.to);
    const [data, setData] = useState<ReturnListPage | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [newReturn, setNewReturn] = useState(false);

    const readOnly = useReadOnly();
    const { authoritative, canWrite } = useAccess();
    const writer = !readOnly && (authoritative ? canWrite("admin.returns") : isManagerPlus(readUserPosition()));

    const dateFmt = useMemo(() => new Intl.DateTimeFormat(language === "pl" ? "pl-PL" : "en-GB", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    }), [language]);

    const update = useCallback((patch: Record<string, string | null>) => {
        setParams((prev) => {
            const next = new URLSearchParams(prev);
            for (const [k, v] of Object.entries(patch)) {
                if (v === null || v === "") next.delete(k); else next.set(k, v);
            }
            return next;
        });
    }, [setParams]);

    // The Errors tab lists no requests, but the request tabs keep their counts: one row is read for them.
    const listPage = onErrors ? 1 : page;
    const listSize = onErrors ? 1 : PAGE_SIZE;
    useEffect(() => {
        let alive = true;
        setLoading(true);
        listReturns({ tab, q: applied.q || undefined, from: applied.from || undefined, to: applied.to || undefined, page: listPage, page_size: listSize })
            .then((r) => {
                if (!alive) return;
                if (r.ok && r.data) { setData(r.data); setError(null); }
                else setError(r.message || t("rt_load_failed"));
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab, applied.q, applied.from, applied.to, listPage, listSize, reloadKey]);

    // The Royal Mail label pool's low-stock warning (Settings › Returns › Royal Mail labels).
    const [pool, setPool] = useState<LabelPoolSummary | null>(null);
    useEffect(() => {
        let alive = true;
        getLabelPoolSummary().then((r) => { if (alive) setPool(r.ok && r.data ? r.data : null); });
        return () => { alive = false; };
    }, [reloadKey]);

    // The Errors tab's badge on every tab: errors nobody has marked seen yet, last 7 days
    // (counts.unseen, whatever the filters; a one-row read). A BE without the endpoint leaves it off.
    const [errorsUnseen, setErrorsUnseen] = useState<number | null>(null);
    useEffect(() => {
        let alive = true;
        getReturnErrors({ page_size: 1 }).then((r) => { if (alive && r.ok && r.data) setErrorsUnseen(unseenErrorCount(r.data)); });
        return () => { alive = false; };
    }, [reloadKey]);

    const searchRef = useRef<HTMLInputElement>(null);
    // The cursor back in the search box with its text selected: the next scan replaces it.
    const refocusSearch = useCallback(() => {
        const el = searchRef.current;
        if (el) { el.focus(); el.select(); }
    }, []);

    const submitSearch = (e: FormEvent) => {
        e.preventDefault();
        update({ q: search.trim(), from, to, page: null });
        if (document.activeElement === searchRef.current) searchRef.current?.select();
    };
    const clearSearch = () => {
        setSearch(""); setFrom(""); setTo("");
        update({ q: null, from: null, to: null, page: null });
    };

    const total = data?.total ?? 0;
    const firstRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const lastRow = Math.min(total, page * PAGE_SIZE);
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="page-data page-data-wide rt-page" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
            <AdminPageHeader titleKey="rt_title" crumbs={[{ labelKey: "admin_panel", to: "/admin" }]} backTo="/admin" actions={writer ? (
                <button type="button" className="btn btn-sm rt-btn btn-primary" onClick={() => setNewReturn(true)}>
                    <Plus size={14} /> {t("rt_new_button")}
                </button>
            ) : undefined} />
            <p className="rt-intro">{t("rt_intro")}</p>
            <LowStockWarning summary={pool} />

            <div className="rt-tabs" role="tablist" aria-label={t("rt_title")}>
                {RETURN_TABS.map((id) => (
                    <button key={id} type="button" role="tab" aria-selected={!onErrors && tab === id}
                        className={`rt-tab${!onErrors && tab === id ? " rt-tab--on" : ""}`}
                        onClick={() => update({ tab: id, page: null, area: null, days: null, seen: null })}>
                        {t(`rt_tab_${id}`)}
                        {data?.counts && <span className="rt-tab-count">{data.counts[id] ?? 0}</span>}
                    </button>
                ))}
                <span className="rt-tabs-sep" aria-hidden="true" />
                <button type="button" role="tab" aria-selected={onErrors}
                    className={`rt-tab rt-tab--errors${onErrors ? " rt-tab--on" : ""}`}
                    onClick={() => update({ tab: "errors", page: null })}>
                    {t("rt_tab_errors")}
                    {errorsUnseen !== null && (
                        <span className={`rt-tab-count${errorsUnseen > 0 ? " rt-tab-count--warn" : ""}`} title={t("rt_err_badge_title")}
                            data-testid="rt-errors-badge">{errorsUnseen}</span>
                    )}
                </button>
            </div>

            {onErrors && (
                <ReturnErrors
                    area={errArea}
                    days={errDays}
                    seen={errSeen}
                    page={page}
                    reloadKey={reloadKey}
                    canWrite={writer}
                    selectedRef={reference}
                    onFilter={update}
                    onOpenRequest={(ref) => update({ ref })}
                    onRefresh={() => setReloadKey((k) => k + 1)}
                    onCounts={setErrorsUnseen}
                />
            )}

            {!onErrors && (<>
                {/* What the tab is for, in one line (2026-11-04). */}
                <p className="rt-tab-hint" data-testid="rt-tab-hint">{t(`rt_tab_hint_${tab}`)}</p>
                <form className="rt-filters" onSubmit={submitSearch}>
                    <div className="rt-search">
                        <Search size={16} aria-hidden="true" />
                        <input ref={searchRef} type="search" className="rt-input" value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("rt_search_placeholder")} aria-label={t("rt_search_placeholder")} />
                    </div>
                    <label className="rt-date">
                        <span>{t("rt_from")}</span>
                        <input type="date" className="rt-input" value={from} onChange={(e) => setFrom(e.target.value)} />
                    </label>
                    <label className="rt-date">
                        <span>{t("rt_to")}</span>
                        <input type="date" className="rt-input" value={to} onChange={(e) => setTo(e.target.value)} />
                    </label>
                    <button type="submit" className="btn btn-sm rt-btn btn-primary">{t("search")}</button>
                    {(applied.q || applied.from || applied.to) && (
                        <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={clearSearch}>{t("rt_clear")}</button>
                    )}
                    <button type="button" className="rt-icon-btn" onClick={() => setReloadKey((k) => k + 1)} aria-label={t("rt_refresh")} title={t("rt_refresh")}>
                        <RefreshCw size={16} className={loading ? "rt-spin" : undefined} />
                    </button>
                </form>

                {applied.q && <ReturnedParcelPanel code={applied.q} canWrite={writer} onRefunded={refocusSearch} />}

                {error && <p className="rt-error" role="alert">{error}</p>}

                <div className="rt-table" role="table" aria-busy={loading}>
                    <div className="rt-tr rt-tr--head" role="row">
                        <span role="columnheader">{t("rt_col_request")}</span>
                        <span role="columnheader">{t("rt_col_customer")}</span>
                        <span role="columnheader">{t("rt_col_items")}</span>
                        <span role="columnheader">{t("rt_col_outcome")}</span>
                        <span role="columnheader" className="rt-num">{t("rt_col_refund")}</span>
                        <span role="columnheader">{t("rt_col_status")}</span>
                    </div>
                    {data && data.rows.length === 0 && !loading && (
                        <p className="rt-empty">{applied.q || applied.from || applied.to ? t("rt_empty_search") : t(`rt_empty_${tab}`)}</p>
                    )}
                    {data?.rows.map((r) => (
                        <button key={r.reference} type="button" role="row" className={`rt-tr${reference === r.reference ? " rt-tr--on" : ""}`}
                            onClick={() => update({ ref: r.reference })}>
                            <span role="cell" className="rt-cell-ref">
                                <span className="rt-ref-line">
                                    <strong className="rt-mono">{r.reference}</strong>
                                    {r.entry === "staff" && <span className="rt-pill rt-pill--plain rt-pill--sm" data-testid="rt-manual-tag">{t("rt_manual_tag")}</span>}
                                </span>
                                <span className="rt-muted">{dateFmt.format(new Date(r.created_at))}</span>
                            </span>
                            <span role="cell" className="rt-cell-cust">
                                <span className="rt-ellipsis">{r.email}</span>
                                <span className="rt-muted">{r.orders.map((o) => `#${o}`).join(", ")}</span>
                            </span>
                            <span role="cell" className="rt-cell-items">
                                <span className="rt-ellipsis">{r.first_title ?? "-"}</span>
                                <span className="rt-muted">
                                    {t("rt_units", { n: String(r.units) })}{r.lines > 1 ? ` · ${t("rt_more_lines", { n: String(r.lines - 1) })}` : ""}
                                    {r.photos > 0 && <> · <Camera size={12} aria-label={t("rt_has_photos")} /> {r.photos}</>}
                                </span>
                            </span>
                            <span role="cell" className="rt-cell-outcome">
                                <span>{problemLabel(t, r.problem)}</span>
                                <span className="rt-muted">{resolutionLabel(t, r.resolution)}{r.claim ? ` · ${t("rt_flag_claim")}` : ""}</span>
                                {r.exchange_shortfall_pence ? <span className="rt-pill rt-pill--amber">{t("rt_flag_shortfall", { short: gbp(r.exchange_shortfall_pence) })}</span> : null}
                            </span>
                            <span role="cell" className="rt-num">
                                {gbp(r.refund_total_pence)}
                                {/* What the customer paid us is not a refund: shown apart, so the two aren't mixed up. */}
                                {r.payable_pence > 0 && <span className="rt-block rt-muted rt-small">{t("rt_row_customer_paid", { amount: gbp(r.payable_pence) })}</span>}
                            </span>
                            <span role="cell" className="rt-cell-status">
                                <StatusPill status={r.status} t={t} />
                                {r.parcels[0]?.tracking_number && <span className="rt-mono rt-muted rt-small">{r.parcels[0].tracking_number}</span>}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="rt-pager">
                    <span className="rt-muted">{t("rt_showing", { from: String(firstRow), to: String(lastRow), total: String(total) })}</span>
                    <span className="rt-pager-btns">
                        <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={page <= 1 || loading} onClick={() => update({ page: String(page - 1) })}>
                            <ChevronLeft size={14} /> {t("rt_prev")}
                        </button>
                        <span className="rt-muted">{t("rt_page_of", { page: String(page), pages: String(pages) })}</span>
                        <button type="button" className="btn btn-sm rt-btn btn-ghost" disabled={page >= pages || loading} onClick={() => update({ page: String(page + 1) })}>
                            {t("rt_next")} <ChevronRight size={14} />
                        </button>
                    </span>
                </div>
            </>)}

            {reference && (
                <ReturnDetailPanel
                    reference={reference}
                    canWrite={writer}
                    onClose={() => update({ ref: null })}
                    onChanged={() => setReloadKey((k) => k + 1)}
                    onOpen={(ref) => update({ ref })}
                />
            )}

            {newReturn && (
                <NewReturnDialog
                    onClose={() => setNewReturn(false)}
                    onCreated={(ref) => {
                        setNewReturn(false);
                        setReloadKey((k) => k + 1);
                        update({ ref });
                    }}
                />
            )}
        </div>
    );
};

export default AdminReturns;
